
import React, { useState, useEffect, useMemo, useRef, Suspense } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, ChevronDown, ChevronUp, Loader2, ShieldAlert } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { supabase, checkAuthStatus } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import { useLegalConsent } from '../../hooks/useLegalConsent'
// 🚀 V209.4: Lazy-load legal modals to reduce initial JS bundle
const TermsModal = React.lazy(() => import('../modals/TermsModal'))
const PrivacyModal = React.lazy(() => import('../modals/PrivacyModal'))
const GuidelinesModal = React.lazy(() => import('../modals/GuidelinesModal'))
const SafetyModal = React.lazy(() => import('../modals/SafetyModal'))
import { rehydratePendingInviteContext, logInviteStorageState } from '@/utils/inviteStorage'
import { requestIdleCallbackPolyfill } from '@/utils/requestIdleCallback'
import { IntegrityService } from '@/services/security/IntegrityService'
import { toast } from 'sonner'
const AUTH_BRAND_ICON_SRC = '/blissnet-master-1024.png'
import { logSignInClicked, logOAuthOpened } from '@/utils/authDiagLogger'
import EmailSignInForm from '@/components/auth/EmailSignInForm'
import { LanguageSelector } from '@/components/common/LanguageSelector'
import { App as CapApp } from '@capacitor/app'
import { Capacitor, type PluginListenerHandle } from '@capacitor/core'
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth'
import {
  blockPrematureIOSOAuthClose,
} from '@/lib/iosOAuthLock'
import {
  getAppleSignInErrorMessage,
  shouldShowEmailSignInOnAuthScreen,
} from '@/lib/reviewer-auth'
import appleLogoSvg from '@/assets/icons/apple-logo.svg?raw'

// V314.5 FINAL: Play Integrity forensic audit type (no PII; tokenLen/keys/verdict/reason/checkId/err only)
type IntegrityAudit = {
  verdict: 'PASSED' | 'FAILED' | 'SKIPPED';
  reason: string;
  checkId?: string;
  ts: number;
  stage: 'nonce' | 'token' | 'invoke' | 'done';
  tokenPresent: boolean;
  tokenLen: number;
  serviceResultKeys: string[];
  err?: string;
};

// V314.5 FINAL: Unified PI timeouts (single source of truth)
const PI_TOTAL_BUDGET_MS = 2200;   // token + verify invoke total cap
const PI_INVOKE_BUDGET_MS = 1200;  // cap on edge function call

const IOS_GOOGLE_CLIENT_ID =
  '25842299623-pju8nivj39ej05mce9unedi4ru8vg5sh.apps.googleusercontent.com'
const GOOGLE_WEB_CLIENT_ID =
  import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID?.trim() ||
  import.meta.env.VITE_GOOGLE_CLIENT_ID?.trim() ||
  ''
const GOOGLE_IOS_AUDIENCE_CONFIG_ERROR =
  'Google sign-in is not configured correctly. Please try Apple sign-in or contact support.'
const GOOGLE_IOS_TEMPORARILY_UNAVAILABLE_ERROR =
  'Google sign-in is temporarily unavailable. Please use Apple sign-in.'
const SAFE_GOOGLE_JWT_ISSUERS = new Set([
  'accounts.google.com',
  'https://accounts.google.com',
])

type DecodedGoogleJwtClaims = {
  aud?: string | string[]
  azp?: string
  iss?: string
  email_verified?: boolean | string
}

function parseConfiguredGoogleClientIds(): Set<string> {
  const configured = new Set<string>([IOS_GOOGLE_CLIENT_ID])
  if (GOOGLE_WEB_CLIENT_ID) {
    configured.add(GOOGLE_WEB_CLIENT_ID)
  }
  const csv = import.meta.env.VITE_GOOGLE_ALLOWED_CLIENT_IDS || ''
  for (const part of csv.split(',')) {
    const value = part.trim()
    if (value) configured.add(value)
  }
  return configured
}

const CONFIGURED_GOOGLE_CLIENT_IDS = parseConfiguredGoogleClientIds()

function normalizeGoogleJwtAudiences(aud: unknown): string[] {
  if (Array.isArray(aud)) {
    return aud
      .map((value) => (typeof value === 'string' ? value.trim() : ''))
      .filter(Boolean)
  }
  if (typeof aud === 'string' && aud.trim().length > 0) {
    return [aud.trim()]
  }
  return []
}

function decodeGoogleJwtClaims(token: string): DecodedGoogleJwtClaims | null {
  try {
    const parts = token.split('.')
    if (parts.length < 2) return null
    const payload = parts[1]
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
    const json = atob(padded)
    const parsed = JSON.parse(json) as DecodedGoogleJwtClaims
    if (!parsed || typeof parsed !== 'object') return null
    return parsed
  } catch {
    return null
  }
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('timeout')), ms);
    promise
      .then((v) => { clearTimeout(t); resolve(v); })
      .catch((e) => { clearTimeout(t); reject(e); });
  });
}

// V411.AUTH-OAUTH-IOS: Module-level mirror of the React `activeOAuthProviderRef` so
// module-scope helpers (closeIOSBrowserSafe) can apply provider guards independently
// of React lifecycle / hook scope. Updated everywhere we set the React ref.
// Exported as a const-shaped object so `.current` is mutable from anywhere.
const __activeOAuthProvider: { current: 'google' | 'apple' | null } = { current: null }

// V405.AUTH-OAUTH-IOS: Force-close in-app SFSafariViewController on iOS OAuth reset paths.
// CAPBrowserPlugin.open rejects with literal "Unable to display URL" when its internal
// `safariViewController` ivar is non-nil (Browser.swift prepare() returns false).
// V401–V404 reset paths only reset React/hook state and never dismiss the SafariVC, so
// the reference leaks after a watchdog/timeout/foreground-fallback fires and every
// subsequent tap fails. Browser.close() routes through cleanup() which clears the ivar.
// "No active window to close!" rejection is expected when SafariVC was already dismissed.
async function closeIOSBrowserSafe(reason: string): Promise<void> {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') return

  // V411: HARD CENTRALIZED BLOCK — last line of defense against stale apple_* timers
  // / leaked listeners that fire during a Google flow (or vice versa). Even if a stale
  // setTimeout slips past every per-callsite guard, this kill-switch refuses to call
  // Browser.close() unless the close-reason actually matches the active provider.
  if (
    reason.startsWith('apple_') &&
    __activeOAuthProvider.current !== 'apple'
  ) {
    console.error('[AUTH] closeIOSBrowserSafe BLOCKED apple reason during non-apple flow', {
      reason,
      activeProvider: __activeOAuthProvider.current,
    })
    return
  }
  if (
    reason.startsWith('google_') &&
    __activeOAuthProvider.current !== 'google'
  ) {
    console.error('[AUTH] closeIOSBrowserSafe BLOCKED google reason during non-google flow', {
      reason,
      activeProvider: __activeOAuthProvider.current,
    })
    return
  }

  console.error('[AUTH] close candidate lock state', {
    reason,
    pending: (window as any).__BLISSNET_IOS_OAUTH_PENDING__ === true,
    ts: (window as any).__BLISSNET_IOS_OAUTH_TS__ || null,
    now: Date.now(),
    activeProvider: __activeOAuthProvider.current,
  })
  try {
    localStorage.setItem(
      'blissnet_ios_oauth_close_candidate',
      reason + ':' + String((window as any).__BLISSNET_IOS_OAUTH_PENDING__) + ':' + Date.now()
    )
  } catch {}
  console.error('[AUTH] Browser.close candidate reason=' + reason)
  if (blockPrematureIOSOAuthClose('AuthenticationStep_closeIOSBrowserSafe_' + reason)) {
    return
  }
  try {
    const { Browser } = await import('@capacitor/browser')
    await Browser.close()
    console.log(`[V405.AUTH-OAUTH-IOS] BROWSER_CLOSE_OK reason=${reason}`)
  } catch {
    console.log(`[V405.AUTH-OAUTH-IOS] BROWSER_CLOSE_SKIP reason=${reason}`)
  }
}

// V314.5 FINAL: Persist audit to localStorage (last_pi_audit + pi_audit_history max 20); must not throw
function persistAudit(audit: IntegrityAudit): void {
  try {
    localStorage.setItem('last_pi_audit', JSON.stringify(audit));
    // History array max 20 entries
    const historyRaw = localStorage.getItem('pi_audit_history');
    let history: IntegrityAudit[] = [];
    try {
      history = historyRaw ? JSON.parse(historyRaw) : [];
    } catch { history = []; }
    history.unshift(audit);
    if (history.length > 20) history = history.slice(0, 20);
    localStorage.setItem('pi_audit_history', JSON.stringify(history));
  } catch (_) { /* localStorage full or unavailable */ }
}

// V314.5 FINAL: nonce -> token -> (optional invoke); never invoke if token missing/invalid; persist on ALL paths
async function runIntegrityAuditBeforeOAuth(): Promise<IntegrityAudit> {
  const ts = Date.now();

  // Base audit object with all forensic fields
  const baseAudit = (): Partial<IntegrityAudit> => ({
    ts,
    tokenPresent: false,
    tokenLen: 0,
    serviceResultKeys: [],
  });

  const skipped = (reason: string, extras?: Partial<IntegrityAudit>): IntegrityAudit => {
    const audit: IntegrityAudit = { verdict: 'SKIPPED', reason, stage: 'done', ...baseAudit(), ...extras } as IntegrityAudit;
    persistAudit(audit);
    return audit;
  };
  const passed = (reason: string, checkId?: string, extras?: Partial<IntegrityAudit>): IntegrityAudit => {
    const audit: IntegrityAudit = { verdict: 'PASSED', reason, checkId, stage: 'done', ...baseAudit(), ...extras } as IntegrityAudit;
    persistAudit(audit);
    return audit;
  };
  const failed = (reason: string, checkId?: string, extras?: Partial<IntegrityAudit>): IntegrityAudit => {
    const audit: IntegrityAudit = { verdict: 'FAILED', reason, checkId, stage: 'done', ...baseAudit(), ...extras } as IntegrityAudit;
    persistAudit(audit);
    return audit;
  };

  try {
    return await withTimeout(
      (async (): Promise<IntegrityAudit> => {
        // Stage: nonce
        let nonce: string;
        try {
          nonce = await IntegrityService.generateNonce('google-oauth-init');
        } catch (e) {
          return skipped('nonce_error', { stage: 'nonce', err: e instanceof Error ? e.message : 'unknown' });
        }

        // Stage: token
        let result: Awaited<ReturnType<typeof IntegrityService.requestIntegrityToken>>;
        try {
          result = await IntegrityService.requestIntegrityToken(nonce);
        } catch (e) {
          return skipped('token_error', { stage: 'token', err: e instanceof Error ? e.message : 'unknown' });
        }

        // V314.4: Capture keys from IntegrityService result for debugging (no PII)
        const serviceResultKeys = result ? Object.keys(result) : [];

        // V314.4: Robust token extraction across possible shapes (cast via unknown for safety)
        const resultAny = result as unknown as Record<string, unknown>;
        const token: string | null =
          result?.token ??
          (typeof resultAny?.integrityToken === 'string' ? resultAny.integrityToken : null) ??
          (typeof resultAny?.integrity_token === 'string' ? resultAny.integrity_token : null) ??
          (typeof (resultAny?.data as Record<string, unknown>)?.token === 'string' ? (resultAny.data as Record<string, unknown>).token as string : null) ??
          (typeof (resultAny?.data as Record<string, unknown>)?.integrityToken === 'string' ? (resultAny.data as Record<string, unknown>).integrityToken as string : null) ??
          null;

        // V314.4: Token must be a non-empty string with minimum length (sanity check)
        const tokenIsValid = typeof token === 'string' && token.trim().length > 50;
        const tokenLen = typeof token === 'string' ? token.length : 0;
        const tokenPresent = tokenIsValid;

        const auditExtras: Partial<IntegrityAudit> = {
          tokenPresent,
          tokenLen,
          serviceResultKeys,
          err: result?.error ?? undefined,
        };

        // V315.6: No auth-time invoke here – single source of truth is PlayIntegrityService.run('login') on SIGNED_IN.
        // Token audit only (diagnostics); actual verification runs after OAuth completes in useAuth.
        return skipped('deferred_to_login', { stage: 'invoke', ...auditExtras });
      })(),
      PI_TOTAL_BUDGET_MS
    );
  } catch (e) {
    const audit: IntegrityAudit = {
      verdict: 'SKIPPED',
      reason: 'timeout',
      ts,
      stage: 'done',
      tokenPresent: false,
      tokenLen: 0,
      serviceResultKeys: [],
      err: e instanceof Error ? e.message : 'timeout',
    };
    persistAudit(audit);
    return audit;
  }
}

// V314.5 FINAL: Read last_pi_audit; minimal validation (verdict in PASSED/FAILED/SKIPPED, tokenLen number, ts number)
function getLastAudit(): IntegrityAudit | null {
  try {
    const raw = localStorage.getItem('last_pi_audit');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as IntegrityAudit;
    const ok =
      parsed &&
      (parsed.verdict === 'PASSED' || parsed.verdict === 'FAILED' || parsed.verdict === 'SKIPPED') &&
      typeof parsed.tokenLen === 'number' &&
      typeof parsed.ts === 'number';
    return ok ? parsed : null;
  } catch {
    return null;
  }
}

/** OAuth 45s watchdog toast — platform-specific hints (avoid Android-only copy on iOS/web). */
function getOAuthSignInDelayToastMessage(): string {
  switch (Capacitor.getPlatform()) {
    case 'android':
      return 'Sign-in delayed. Check Play Services, WebView, and Chrome, then try again.'
    case 'ios':
      return 'Sign-in delayed. Complete sign-in in the browser, return to the app, and try again.'
    default:
      return 'Sign-in delayed. Complete sign-in in your browser, check your connection, and try again.'
  }
}

// V314.5 FINAL: Token stage only (no invoke); for self-test; always persistAudit before return; nonce null on error
async function runIntegrityAuditOnly(): Promise<{ audit: IntegrityAudit; token: string | null; nonce: string | null }> {
  const ts = Date.now();
  const baseAudit = (): Partial<IntegrityAudit> => ({
    ts,
    tokenPresent: false,
    tokenLen: 0,
    serviceResultKeys: [],
  });

  let nonce: string | null = null;
  try {
    nonce = await IntegrityService.generateNonce('google-oauth-init');
  } catch (e) {
    const audit: IntegrityAudit = {
      verdict: 'SKIPPED',
      reason: 'nonce_error',
      stage: 'nonce',
      err: e instanceof Error ? e.message : 'unknown',
      ...baseAudit(),
    } as IntegrityAudit;
    persistAudit(audit);
    return { audit, token: null, nonce: null };
  }

  let result: Awaited<ReturnType<typeof IntegrityService.requestIntegrityToken>>;
  try {
    result = await IntegrityService.requestIntegrityToken(nonce);
  } catch (e) {
    const audit: IntegrityAudit = {
      verdict: 'SKIPPED',
      reason: 'token_error',
      stage: 'token',
      err: e instanceof Error ? e.message : 'unknown',
      ...baseAudit(),
    } as IntegrityAudit;
    persistAudit(audit);
    return { audit, token: null, nonce: null };
  }

  const serviceResultKeys = result ? Object.keys(result) : [];
  const resultAny = result as unknown as Record<string, unknown>;
  const token: string | null =
    result?.token ??
    (typeof resultAny?.integrityToken === 'string' ? resultAny.integrityToken : null) ??
    (typeof resultAny?.integrity_token === 'string' ? resultAny.integrity_token : null) ??
    (typeof (resultAny?.data as Record<string, unknown>)?.token === 'string' ? (resultAny.data as Record<string, unknown>).token as string : null) ??
    (typeof (resultAny?.data as Record<string, unknown>)?.integrityToken === 'string' ? (resultAny.data as Record<string, unknown>).integrityToken as string : null) ??
    null;

  const tokenIsValid = typeof token === 'string' && token.trim().length > 50;
  const tokenLen = typeof token === 'string' ? token.length : 0;
  const tokenPresent = tokenIsValid;

  const audit: IntegrityAudit = {
    verdict: tokenIsValid ? 'PASSED' : 'SKIPPED',
    reason: tokenIsValid ? 'token_ok' : 'no_token',
    stage: 'token',
    tokenPresent,
    tokenLen,
    serviceResultKeys,
    err: result?.error ?? undefined,
    ...baseAudit(),
  } as IntegrityAudit;

  persistAudit(audit);
  return { audit, token: tokenIsValid ? token : null, nonce };
}

const AuthenticationStep: React.FC = () => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [activeAuthProvider, setActiveAuthProvider] = useState<'google' | 'apple' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showTerms, setShowTerms] = useState(false)
  const [showPrivacy, setShowPrivacy] = useState(false)
  const [showGuidelines, setShowGuidelines] = useState(false)
  const [showSafety, setShowSafety] = useState(false)
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [debugAudit, setDebugAudit] = useState<IntegrityAudit | null>(null)
  const [piTestRunning, setPiTestRunning] = useState(false)
  const showEmailSignInSection = shouldShowEmailSignInOnAuthScreen()

  /** Email/password sign-in — routes to useAuth.signIn (Apple review account uses apple-review-auth edge function). */
  const [emailSignInEmail, setEmailSignInEmail] = useState('')
  const [emailSignInPassword, setEmailSignInPassword] = useState('')
  const [emailSignInLoading, setEmailSignInLoading] = useState(false)
  /** Default open in review mode so App Review sees email/password without hunting. */
  const [showEmailSignIn, setShowEmailSignIn] = useState(true)
  const [emailSignInPendingConsent, setEmailSignInPendingConsent] = useState(false)
  const emailConsentSubmittingRef = useRef(false)

  const location = useLocation()
  const navigate = useNavigate()
  const { user, session, isReady, appleSignIn, signIn, resetAuthInProgress, beginIntentionalAuth } = useAuth()

  // V331.2: 45s OAuth watchdog for delayed callback / session resolution
  const signInWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Which OAuth path owns the in-flight native browser + watchdog (prevents Apple watchdog closing Google). */
  const activeOAuthProviderRef = useRef<'google' | 'apple' | null>(null)
  const appleBrowserDismissListenerRef = useRef<PluginListenerHandle | null>(null)
  const googleBrowserDismissListenerRef = useRef<PluginListenerHandle | null>(null)
  const userRef = useRef(user)
  userRef.current = user
  const callbackReceivedRef = useRef(false)

  // V314.5 FINAL: pi_debug-only UI — enable via ?pi_debug=1 (stored in localStorage.pi_debug)
  const piDebug = useMemo(() => {
    if (!import.meta.env.DEV || typeof window === 'undefined') return false
    const p = new URLSearchParams(window.location.search)
    if (p.get('pi_debug') === '1') localStorage.setItem('pi_debug', '1')
    return localStorage.getItem('pi_debug') === '1'
  }, [])

  // V180.5: Use production-ready legal consent hook
  const {
    loading: consentLoading,
    saving: consentSaving,
    hasAcceptedAll,
    activeDocs,
    acceptAll,
  } = useLegalConsent('en')

  const inviteCodeFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return params.get('inviteCode')
  }, [location.search])

  // V331: Clear OAuth watchdog timer on unmount or when session/user exists (SIGNED_IN + session restore)
  useEffect(() => {
    if (user || session) {
      callbackReceivedRef.current = true
      activeOAuthProviderRef.current = null
      __activeOAuthProvider.current = null
      setIsLoading(false)
      setActiveAuthProvider(null)
      if (signInWatchdogRef.current) {
        clearTimeout(signInWatchdogRef.current)
        signInWatchdogRef.current = null
      }
    }
    return () => {
      activeOAuthProviderRef.current = null
      __activeOAuthProvider.current = null
      if (signInWatchdogRef.current) {
        clearTimeout(signInWatchdogRef.current)
        signInWatchdogRef.current = null
      }
    }
  }, [user, session])

  // V331.2 / V403 / V410: When app regains focus during an OAuth flow without a callback, replace the
  // long 45s watchdog with a short 6s secondary fallback. Previously this listener fired the 6s
  // timer but did NOT reset the UI on no-session — leaving the spinner stuck forever even after
  // a clear cancel/error path. V410 mirrors the 45s-watchdog cleanup so the user can retry.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return
    const listener = CapApp.addListener('appStateChange', ({ isActive }) => {
      if (!isActive || callbackReceivedRef.current || userRef.current) return
      if (!signInWatchdogRef.current) return
      // V410+: iOS — never arm 6s foreground fallback (it cleared the 45s watchdog and closed Browser).
      if (Capacitor.getPlatform() === 'ios') {
        console.error('[AUTHFLOW] foreground_no_callback arming skipped on iOS (no 6s fallback)')
        return
      }
      console.error('[AUTHFLOW] foreground_no_callback arming 6s fallback')
      clearTimeout(signInWatchdogRef.current)
      signInWatchdogRef.current = setTimeout(async () => {
        signInWatchdogRef.current = null
        if (callbackReceivedRef.current || userRef.current) return
        try {
          const { data } = await supabase.auth.getSession()
          if (data?.session) { callbackReceivedRef.current = true; return }
        } catch { /* ignore probe error */ }
        if (Capacitor.getPlatform() === 'ios') {
          console.error('[AUTHFLOW] foreground_no_callback 6s elapsed — ignored for iOS OAuth')
          return
        }
        console.error('[AUTHFLOW] foreground_no_callback 6s elapsed — resetting UI')
        try { await closeIOSBrowserSafe('ios_foreground_no_callback_6s') } catch {}
        setIsLoading(false)
        setActiveAuthProvider(null)
        try { resetAuthInProgress('ios_foreground_no_callback_6s') } catch {}
        setError('Sign in did not complete. Please try again.')
        try { toast.error('Sign in did not complete. Please try again.', { duration: 6000 }) } catch {}
      }, 6_000)
    })
    return () => { listener.then(l => l.remove()) }
  }, [])

  // V411: Web-only back-forward cache recovery. On web, Supabase signInWithOAuth navigates
  // window.location to the provider (Apple/Google). If the provider rejects (e.g. Apple
  // invalid_request) or the user cancels and presses Back, Safari/Chrome may restore this page
  // from bfcache with stale loading state — leaving "Connecting..." active and the auth buttons
  // disabled. Reset the loading state machine on persisted pageshow when no session exists so the
  // buttons become usable again. Guarded to web; native uses the appStateChange listener above.
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted || userRef.current) return
      console.error('[AUTHFLOW] web bfcache restore — resetting OAuth loading state')
      if (signInWatchdogRef.current) {
        clearTimeout(signInWatchdogRef.current)
        signInWatchdogRef.current = null
      }
      activeOAuthProviderRef.current = null
      __activeOAuthProvider.current = null
      callbackReceivedRef.current = false
      setIsLoading(false)
      setActiveAuthProvider(null)
      try { resetAuthInProgress('web_bfcache_restore') } catch { /* ignore */ }
    }
    window.addEventListener('pageshow', onPageShow)
    return () => window.removeEventListener('pageshow', onPageShow)
  }, [])

  // V314.6: Load last audit on mount when piDebug is enabled (for immediate banner visibility)
  useEffect(() => {
    if (!import.meta.env.DEV || !piDebug) return

    const lastAudit = getLastAudit()
    if (lastAudit) {
      setDebugAudit(lastAudit)
    } else {
      // No audit exists yet - set placeholder so banner shows immediately
      setDebugAudit({
        verdict: 'SKIPPED',
        reason: 'pi_debug_enabled',
        stage: 'done',
        tokenPresent: false,
        tokenLen: 0,
        serviceResultKeys: [],
        ts: Date.now(),
      } as IntegrityAudit)
    }
  }, [piDebug])

  // V314.5 FINAL: Self-test runner — token stage first, then invoke if token present (no OAuth)
  const runIntegritySelfTest = async () => {
    if (!import.meta.env.DEV || !piDebug || piTestRunning) return
    setPiTestRunning(true)
    setDebugAudit({
      verdict: 'SKIPPED',
      reason: 'running...',
      stage: 'token',
      tokenPresent: false,
      tokenLen: 0,
      serviceResultKeys: [],
      ts: Date.now(),
    } as IntegrityAudit)

    try {
      const { audit, token, nonce } = await runIntegrityAuditOnly()
      let finalAudit: IntegrityAudit = { ...audit }

      // V315.6: Single source of truth – use PlayIntegrityService.run('self_test') instead of direct invoke
      if (token && nonce && piDebug) {
        try {
          const { PlayIntegrityService } = await import('@/services/PlayIntegrityService')
          // V316.6: Added client_source tag to prove call-site
          const r = await PlayIntegrityService.run('self_test', 'auth_self_test')
          if (r != null) {
            const reason = (typeof r.reason === 'string' ? r.reason : '') || (r.ok ? 'ok' : 'fail')
            finalAudit = {
              ...audit,
              verdict: r.ok ? 'PASSED' : 'FAILED',
              reason,
              stage: 'done',
              checkId: r.check_id ?? undefined,
            }
          } else {
            finalAudit = { ...audit, stage: 'invoke', reason: 'skipped', err: 'shouldRun=false or rate limit' }
          }
        } catch (e) {
          finalAudit = { ...audit, stage: 'invoke', reason: 'invoke_error', err: (e as Error)?.message ?? 'exception' }
        }
      }

      persistAudit(finalAudit)
      setDebugAudit(finalAudit)
      toast.info(`PI: ${finalAudit.verdict} · tokenLen:${finalAudit.tokenLen}`)
    } finally {
      setPiTestRunning(false)
    }
  }

  const showLastAudit = () => {
    if (!import.meta.env.DEV || !piDebug) return
    const last = getLastAudit()
    if (last) setDebugAudit(last)
    else toast.info('No last audit in localStorage')
  }

  // 🚨 CRITICAL FIX: Check authentication and navigate appropriately using useAuth hook
  // V180: Added legal consent checking
  // V209.7: Defer non-critical console logs to idle callback
  useEffect(() => {
    const checkAuthAndRedirect = async () => {
      // V167.INVITE.FALLBACK: Detect and store invite code from multiple sources
      // Check URL params
      const urlParams = new URLSearchParams(window.location.search);
      const inviteCodeFromQuery = urlParams.get('inviteCode');
      // Persist ?redirect= so it survives OAuth round-trip (e.g. /auth?redirect=/bar-ilan/dashboard)
      const redirectParam = urlParams.get('redirect');
      if (redirectParam && redirectParam.startsWith('/')) {
        sessionStorage.setItem('authRedirect', redirectParam);
      }

      // Check path (in case user lands on /invite/XXX then redirects to /auth)
      // V209.11: document.referrer is not a layout read (no reflow), safe to use directly
      const referrer = document.referrer;
      const inviteCodeFromReferrer = referrer ? referrer.split('/invite/')[1]?.split(/[?#]/)[0] : null;

      // Check if already stored
      const storedCode = localStorage.getItem('pendingInviteCode') || sessionStorage.getItem('pendingInviteCode');

      const inviteCode = inviteCodeFromQuery || inviteCodeFromReferrer || storedCode;

      if (import.meta.env.DEV) {
        requestIdleCallbackPolyfill(() => {
          console.info('AUTH_INVITE', {
            route: inviteCodeFromQuery ? 'auth_with_invite' : 'auth',
            hasUser: !!user,
            isReady,
            hasInviteFromQuery: !!inviteCodeFromQuery,
            hasInviteFromReferrer: !!inviteCodeFromReferrer,
            hasInviteFromStorage: !!storedCode,
            hasInvite: !!inviteCode,
          });
        }, { timeout: 1000 });
      }

      if (inviteCode && !storedCode) {
        // Store in BOTH places (critical - keep synchronous)
        localStorage.setItem('pendingInviteCode', inviteCode);
        sessionStorage.setItem('pendingInviteCode', inviteCode);
        localStorage.setItem('inviteCodeTimestamp', new Date().toISOString());
        sessionStorage.setItem('inviteCodeTimestamp', new Date().toISOString());

        if (import.meta.env.DEV) {
          requestIdleCallbackPolyfill(() => {
            console.info('AUTH_INVITE', { action: 'deferred_invite_present', stored: true });
          }, { timeout: 1000 });
        }
      } else if (inviteCode && storedCode) {
        if (import.meta.env.DEV) {
          requestIdleCallbackPolyfill(() => {
            console.info('AUTH_INVITE', { action: 'deferred_invite_present', alreadyStored: true });
          }, { timeout: 1000 });
        }
      } else {
        if (import.meta.env.DEV) {
          requestIdleCallbackPolyfill(() => {
            console.info('AUTH_INVITE', { action: 'deferred_invite_absent' });
          }, { timeout: 1000 });
        }
      }

      // Attempt to rehydrate invite data if storage was cleared during OAuth
      await rehydratePendingInviteContext('🚨 V167.PUSH.FIX11: AuthenticationStep rehydrate', inviteCode || inviteCodeFromQuery);

      // Log current storage snapshot after potential rehydrate
      logInviteStorageState('🚨 V167.PUSH.FIX11: AuthenticationStep post-rehydrate');

      // Wait for auth to be ready
      if (!isReady) {
        console.log('🔍 AuthenticationStep: Auth not ready yet, waiting...')
        return
      }

      if (user) {
        console.log('✅ User authenticated:', user.email, 'checking profile completion...')

        // V180.5: Check legal consent status using hook
        if (!consentLoading && !hasAcceptedAll) {
          console.log('⚠️ V180.5: User missing consent, staying on auth screen');
          // User needs to accept - stay on consent screen (don't navigate yet)
          // The consent checkbox will handle recording when user continues
          return;
        } else if (!consentLoading && hasAcceptedAll) {
          console.log('✅ V180.5: User has valid consent, proceeding to profile check');
        }

        // V167.FIX2: Check if user has completed onboarding
        // Use maybeSingle() to handle new users without database records
        try {
          const { data: userProfile, error } = await supabase
            .from('users')
            .select('id, name, photo_urls')
            .eq('id', user.id)
            .maybeSingle()

          if (error) {
            console.error('⚠️ AuthenticationStep: Error fetching user profile:', error.message)
            // Error querying - redirect to onboarding to be safe
            // [AuthStep] Distinct from (a)(b)(c): Supabase/RLS/query failure — not necessarily missing row.
            console.log('[AuthStep] redirect → /onboarding: reason=users_query_error', { code: error.code, message: error.message })
            const onboardingPath = inviteCodeFromQuery
              ? `/onboarding?inviteCode=${encodeURIComponent(inviteCodeFromQuery)}`
              : '/onboarding'
            navigate(onboardingPath, { replace: true })
            return
          }

          // If no user record exists, this is a new user
          if (!userProfile) {
            // (a) Missing public.users row for this auth uid (bootstrap lag or first-time user).
            console.log('[AuthStep] redirect → /onboarding: reason=(a)_missing_users_row', { userId: user.id })
            const onboardingPath = inviteCodeFromQuery
              ? `/onboarding?inviteCode=${encodeURIComponent(inviteCodeFromQuery)}`
              : '/onboarding'
            navigate(onboardingPath, { replace: true })
            return
          }

          // Check if profile is complete (has name and at least one photo)
          const hasName = Boolean(userProfile.name)
          const hasPhotos = Boolean(userProfile.photo_urls && userProfile.photo_urls.length > 0)
          const isComplete = hasName && hasPhotos

          if (isComplete) {
            console.log('✅ AuthenticationStep: Profile complete - redirecting to /profile')

            // V167.PUSH.FIX9: Check BOTH localStorage AND sessionStorage for pending invitation
            const inviteChoice = localStorage.getItem('inviteChoice') || sessionStorage.getItem('inviteChoice');
            const pendingInviteContext = localStorage.getItem('pendingInviteContext') || sessionStorage.getItem('pendingInviteContext');

            console.info('AUTH_INVITE', {
              action: 'deferred_invite_check',
              hasInviteChoice: !!inviteChoice,
              hasPendingInviteContext: !!pendingInviteContext,
              inLocalStorage: !!localStorage.getItem('pendingInviteContext'),
              inSessionStorage: !!sessionStorage.getItem('pendingInviteContext'),
            });

            if (inviteChoice === 'connect' && pendingInviteContext) {
              console.info('AUTH_INVITE', { action: 'deferred_invite_present' });

              try {
                const inviteData = JSON.parse(pendingInviteContext);

                // Set up post-onboarding invitation popup
                localStorage.setItem('showPostOnboardingInvitePopup', 'true');
                localStorage.setItem('postOnboardingInviteData', JSON.stringify({
                  ...inviteData,
                  userId: user.id,
                  storedAt: new Date().toISOString()
                }));
                window.dispatchEvent(new CustomEvent('invite-storage-ready'));

                console.info('AUTH_INVITE', { action: 'invite_completed', popupReady: true });

                // Clean up pending data from BOTH storages
                localStorage.removeItem('inviteChoice');
                localStorage.removeItem('pendingInviteContext');
                sessionStorage.removeItem('inviteChoice');
                sessionStorage.removeItem('pendingInviteContext');
              } catch {
                console.warn('AUTH_INVITE', { action: 'parse_error' });
              }
            }

            // Honor ?redirect= (e.g. return to /bar-ilan/dashboard after sign-in)
            const redirect = sessionStorage.getItem('authRedirect') || new URLSearchParams(window.location.search).get('redirect');
            if (redirect && redirect.startsWith('/')) {
              sessionStorage.removeItem('authRedirect');
              navigate(redirect, { replace: true });
            } else {
              navigate('/profile', { replace: true });
            }
          } else {
            // (b) onboarding: missing display name. (c) onboarding: no photos in users.photo_urls.
            const reason =
              !hasName && !hasPhotos ? '(b)_missing_name+(c)_missing_photo' :
              !hasName ? '(b)_incomplete_onboarding_missing_name' :
              '(c)_incomplete_onboarding_missing_photo'
            console.log('[AuthStep] redirect → /onboarding:', reason, { hasName, hasPhotos, photoCount: userProfile.photo_urls?.length ?? 0 })
            const onboardingPath = inviteCodeFromQuery
              ? `/onboarding?inviteCode=${encodeURIComponent(inviteCodeFromQuery)}`
              : '/onboarding'
            navigate(onboardingPath, { replace: true })
          }
        } catch (err) {
          console.error('💥 AuthenticationStep: Error checking profile:', err)
          console.log('[AuthStep] redirect → /onboarding: reason=exception_during_profile_check', err)
          // Fallback to onboarding on error
          const onboardingPath = inviteCodeFromQuery
            ? `/onboarding?inviteCode=${encodeURIComponent(inviteCodeFromQuery)}`
            : '/onboarding'
          navigate(onboardingPath, { replace: true })
        }
      } else {
        console.log('🔍 User not authenticated - staying on auth page')
      }
    }

    checkAuthAndRedirect()
  // hasAcceptedAll + consentLoading included so the effect re-runs after email sign-in
  // saves consent (fixes navigation deadlock on first-ever sign-in of review account).
  }, [user, isReady, navigate, inviteCodeFromQuery, hasAcceptedAll, consentLoading])

  // After email/password sign-in, record legal consent when hook has loaded activeDocs (same as OAuth success path).
  useEffect(() => {
    if (!emailSignInPendingConsent || !user) return
    if (consentLoading) return
    if (hasAcceptedAll) {
      setEmailSignInPendingConsent(false)
      return
    }
    if (activeDocs.length === 0) return
    if (!agreedToTerms) {
      setEmailSignInPendingConsent(false)
      return
    }
    if (emailConsentSubmittingRef.current) return
    emailConsentSubmittingRef.current = true

    void (async () => {
      try {
        await acceptAll()
      } catch (e) {
        console.error('❌ Email sign-in: acceptAll failed', e)
        toast.error('Could not save consent. Please try again.')
      } finally {
        emailConsentSubmittingRef.current = false
        setEmailSignInPendingConsent(false)
      }
    })()
  }, [
    emailSignInPendingConsent,
    user,
    consentLoading,
    hasAcceptedAll,
    activeDocs.length,
    agreedToTerms,
    acceptAll,
  ])

  // 🛠️ V113.12: SIMPLE DIRECT PATHNAME DETECTION - NO COMPLEX LOGIC
  useEffect(() => {
    // SIMPLE AND FOOLPROOF - DIRECT PATHNAME CHECK
    setShowPrivacy(location.pathname === '/privacy')
    setShowTerms(location.pathname === '/terms')
    setShowGuidelines(location.pathname === '/guidelines')
    setShowSafety(location.pathname === '/legal/safety')
  }, [location.pathname])

  // 🛠️ V113.12: SIMPLE MODAL CLOSE HANDLERS
  const handleCloseTerms = () => {
    setShowTerms(false)
    if (location.pathname === '/terms') {
      navigate('/', { replace: true }) // Go back to main auth page
    }
  }

  const handleClosePrivacy = () => {
    setShowPrivacy(false)
    if (location.pathname === '/privacy') {
      navigate('/', { replace: true }) // Go back to main auth page
    }
  }

  const handleCloseGuidelines = () => {
    setShowGuidelines(false)
    if (location.pathname === '/guidelines') {
      navigate('/', { replace: true }) // Go back to main auth page
    }
  }

  const handleCloseSafety = () => {
    setShowSafety(false)
    if (location.pathname === '/legal/safety') {
      navigate('/', { replace: true }) // Go back to main auth page
    }
  }

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1)
      return
    }
    navigate('/', { replace: true })
  }

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!agreedToTerms) {
      toast.error(t('welcome.consentRequired'))
      return
    }
    if (isLoading || consentSaving || emailSignInLoading) return
    beginIntentionalAuth()
    setError(null)
    setEmailSignInLoading(true)
    try {
      logSignInClicked()
      const { error: signErr } = await signIn(emailSignInEmail, emailSignInPassword)
      if (signErr) {
        const msg = signErr.message || 'Sign in failed'
        setError(msg)
        toast.error(msg)
        return
      }
      setEmailSignInPendingConsent(true)
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Sign in failed'
      setError(msg)
      toast.error(msg)
    } finally {
      setEmailSignInLoading(false)
    }
  }

  const handleGoogleAuth = async () => {
    if (!agreedToTerms) {
      toast.error(t('welcome.consentRequired'))
      return
    }

    if (isLoading || consentSaving || emailSignInLoading) return
    beginIntentionalAuth()
    setError(null)
    setActiveAuthProvider('google')
    setIsLoading(true)
    activeOAuthProviderRef.current = 'google'
    __activeOAuthProvider.current = 'google'
    console.error('[AUTH] activeOAuthProvider set google at handler start (activeProvider=google)')
    if (signInWatchdogRef.current) {
      clearTimeout(signInWatchdogRef.current)
      signInWatchdogRef.current = null
    }
    try {
      if (appleBrowserDismissListenerRef.current) {
        await appleBrowserDismissListenerRef.current.remove()
        appleBrowserDismissListenerRef.current = null
      }
    } catch { /* ignore */ }

    // V331: Auth flow instrumentation - high-signal logs for stuck-on-Welcome investigation
    logSignInClicked()

    // V180.5: If user is already authenticated, record consent and proceed
    if (user) {
      try {
        if (!hasAcceptedAll && activeDocs.length > 0) {
          await acceptAll(/* optionally client IP here */)
        }
        navigate('/profile')
        activeOAuthProviderRef.current = null
        __activeOAuthProvider.current = null
        return
      } catch (error) {
        console.error('❌ V180.5: Failed to save consent:', error)
        toast.error('Failed to save consent. Please try again.')
        setIsLoading(false)
        activeOAuthProviderRef.current = null
        __activeOAuthProvider.current = null
        return
      }
    }

    // V314.5 FINAL: Run PI audit before OAuth (audit persisted inside); do not block OAuth on result
    let lastIosGoogleClaims: DecodedGoogleJwtClaims | null = null
    try {
      const audit = await runIntegrityAuditBeforeOAuth()
      if (piDebug) setDebugAudit(audit)

      const { getOAuthRedirectTo } = await import('@/lib/auth-helpers')
      const redirectTo = getOAuthRedirectTo()

      // iOS native: use Codetrix Google Sign-In and exchange Google ID token with Supabase.
      if (Capacitor.getPlatform() === 'ios') {
        console.error('[AUTH] 🔵 Google native start (codetrix)')
        if (!GOOGLE_WEB_CLIENT_ID) {
          console.error(
            '[AUTH_GOOGLE_IOS_CONFIG_MISSING] VITE_GOOGLE_WEB_CLIENT_ID is required for iOS Google login'
          )
          throw new Error(GOOGLE_IOS_TEMPORARILY_UNAVAILABLE_ERROR)
        }

        const googleInitializeOptions: {
          clientId: string
          iosClientId: string
          scopes: string[]
          grantOfflineAccess: boolean
          serverClientId?: string
        } = {
          clientId: IOS_GOOGLE_CLIENT_ID,
          iosClientId: IOS_GOOGLE_CLIENT_ID,
          scopes: ['profile', 'email'],
          grantOfflineAccess: true,
        }
        if (GOOGLE_WEB_CLIENT_ID) {
          googleInitializeOptions.serverClientId = GOOGLE_WEB_CLIENT_ID
        }
        GoogleAuth.initialize(googleInitializeOptions)
        console.error(
          `[AUTH] 🔵 iOS GoogleAuth init iosClientId=${IOS_GOOGLE_CLIENT_ID} serverClientId=${GOOGLE_WEB_CLIENT_ID || 'missing'}`
        )

        let result: Awaited<ReturnType<typeof GoogleAuth.signIn>>
        try {
          result = await GoogleAuth.signIn()
        } catch (googleAuthError) {
          console.error('[AUTH] 🔵 GoogleAuth.signIn() full error', googleAuthError)
          throw googleAuthError
        }
        const idToken = result.authentication?.idToken

        console.error('[AUTH] 🔵 idToken received', !!idToken)

        if (!idToken) {
          throw new Error('Google idToken missing')
        }

        const decodedClaims = decodeGoogleJwtClaims(idToken)
        lastIosGoogleClaims = decodedClaims
        const audiences = normalizeGoogleJwtAudiences(decodedClaims?.aud)
        const hasAllowedAudience = audiences.some((aud) => CONFIGURED_GOOGLE_CLIENT_IDS.has(aud))
        const hasIssuer = typeof decodedClaims?.iss === 'string' && SAFE_GOOGLE_JWT_ISSUERS.has(decodedClaims.iss)

        console.error(
          `[AUTH_GOOGLE_IOS_JWT_CLAIMS] aud=${decodedClaims?.aud ?? 'null'} azp=${decodedClaims?.azp ?? 'null'} iss=${decodedClaims?.iss ?? 'null'} email_verified=${decodedClaims?.email_verified ?? 'null'}`
        )

        if (audiences.length > 0 && !hasAllowedAudience) {
          console.error(
            `[AUTH_GOOGLE_IOS_AUD_MISMATCH] aud=${audiences.join(',')} azp=${decodedClaims?.azp ?? 'unknown'}`
          )
          throw new Error(GOOGLE_IOS_AUDIENCE_CONFIG_ERROR)
        }

        if (!hasIssuer) {
          console.error('[AUTH] 🔵 iOS Google JWT unexpected issuer', {
            iss: decodedClaims?.iss ?? null,
          })
        }

        console.error('[AUTH_GOOGLE_IOS_SUPABASE_CALL] provider=google token=true nonce=false')
        const { error } = await supabase.auth.signInWithIdToken({
          provider: 'google',
          token: idToken,
        })

        console.error('[AUTH] 🔵 Supabase Google success', !error)

        if (error) throw error

        callbackReceivedRef.current = true
        return
      }

      // Android: browser OAuth only for this release (no native Google credential dependency).
      if (Capacitor.getPlatform() === 'android') {
        const androidRedirectTo = 'com.blissnet.app://auth-callback'
        console.error(
          `[AUTH_GOOGLE_ANDROID_OAUTH_BROWSER] redirectTo=${androidRedirectTo}`
        )
        const { data, error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: androidRedirectTo,
            skipBrowserRedirect: true,
            queryParams: {
              access_type: 'offline',
              prompt: 'select_account',
            },
          },
        })
        if (error) throw error
        if (!data?.url) throw new Error('Google OAuth URL missing')
        const { Browser } = await import('@capacitor/browser')
        await Browser.open({ url: data.url, presentationStyle: 'fullscreen' })
        console.error('[AUTH_OAUTH_BROWSER_OPENED] provider=google')
        callbackReceivedRef.current = false
        logOAuthOpened(androidRedirectTo)
        return
      }

      // V330: Web — keep existing behavior (Supabase redirects window.location)
      // V330: Force Google account selection to prevent cached account issues
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo,
          queryParams: {
            access_type: 'offline',
            prompt: 'select_account' // V330: Changed from 'consent' to 'select_account'
          },
        },
      })

      if (error) throw error
      // V331: Auth flow instrumentation
      logOAuthOpened(redirectTo)
      callbackReceivedRef.current = false
      // V331.2: 45s OAuth watchdog (callback-aware); native: also cleared on app resume via appStateChange
      if (signInWatchdogRef.current) clearTimeout(signInWatchdogRef.current)
      signInWatchdogRef.current = setTimeout(async () => {
        signInWatchdogRef.current = null
        if (callbackReceivedRef.current || userRef.current) return
        if (activeOAuthProviderRef.current !== 'google') {
          console.error('[AUTH] google watchdog (web/android) ignored because active provider is', activeOAuthProviderRef.current)
          return
        }
        // Callback arrived but session still loading? Check Supabase session
        try {
          const { data } = await supabase.auth.getSession()
          if (data?.session) {
            callbackReceivedRef.current = true
            return // Soft: keep spinner, no toast
          }
        } catch { /* ignore */ }
        toast.error(getOAuthSignInDelayToastMessage(), { duration: 6000 })
      }, 45_000)
      if (import.meta.env.DEV) console.log('🔄 Google OAuth initiated - redirecting...')
    } catch (error: unknown) {
      let rawError: string
      try {
        rawError = JSON.stringify(error)
      } catch {
        rawError = '[unserializable-error]'
      }
      console.error('[AUTH] 🔵 Firebase Google raw error', {
        name: error && typeof error === 'object' && 'name' in error ? (error as { name?: unknown }).name : undefined,
        message: error && typeof error === 'object' && 'message' in error ? (error as { message?: unknown }).message : undefined,
        code: error && typeof error === 'object' && 'code' in error ? (error as { code?: unknown }).code : undefined,
        stack: error && typeof error === 'object' && 'stack' in error ? (error as { stack?: unknown }).stack : undefined,
        raw: rawError,
      })
      const isTimeout = error instanceof Error && error.message === 'timeout'
      if (signInWatchdogRef.current) {
        clearTimeout(signInWatchdogRef.current)
        signInWatchdogRef.current = null
      }
      console.error('[V403.AUTH-GOOGLE-IOS] HOOK_THROW isTimeout=', isTimeout, 'err=', error)
      const message = isTimeout
        ? 'Sign in took too long. Please check your connection and try again.'
        : (error && typeof error === 'object' && 'message' in error ? String((error as { message: unknown }).message) : 'Google authentication failed')
      const isGoogleAudienceError = message.includes('Unacceptable audience in id_token')
      if (Capacitor.getPlatform() === 'ios' && isGoogleAudienceError) {
        const mismatchAud = normalizeGoogleJwtAudiences(lastIosGoogleClaims?.aud).join(',') || 'unknown'
        const mismatchAzp = lastIosGoogleClaims?.azp || 'unknown'
        console.error(
          `[AUTH_GOOGLE_IOS_AUD_MISMATCH] aud=${mismatchAud} azp=${mismatchAzp}`
        )
      }
      console.log('[V403.AUTH-GOOGLE-IOS] RESET reason=', isTimeout ? 'hook_timeout_20s' : 'hook_throw')
      if (activeOAuthProviderRef.current !== 'google') {
        console.error('[AUTH] google error-path close ignored because active provider is', activeOAuthProviderRef.current)
      } else {
        if (Capacitor.getPlatform() !== 'ios') {
          await closeIOSBrowserSafe(isTimeout ? 'google_ios_timeout' : 'google_ios_throw') // V405: dismiss leaked SafariVC
        }
      }
      activeOAuthProviderRef.current = null
      __activeOAuthProvider.current = null
      setError(
        Capacitor.getPlatform() === 'ios' && isGoogleAudienceError
          ? GOOGLE_IOS_AUDIENCE_CONFIG_ERROR
          : message
      )
      setIsLoading(false)
      setActiveAuthProvider(null)
      resetAuthInProgress(isTimeout ? 'google_ios_timeout' : 'google_ios_throw') // V404: clear hook lock
      if (isTimeout) toast.error(message, { duration: 6000 })
    }
  }

  const handleAppleAuth = async () => {
    if (!agreedToTerms) {
      toast.error(t('welcome.consentRequired'))
      return
    }

    if (isLoading || consentSaving || emailSignInLoading) return
    beginIntentionalAuth()
    console.log('[V402.AUTH-APPLE] APPLE_START platform=', Capacitor.getPlatform(), 'isNative=', Capacitor.isNativePlatform())
    setError(null)
    setActiveAuthProvider('apple')
    setIsLoading(true)
    activeOAuthProviderRef.current = 'apple'
    __activeOAuthProvider.current = 'apple'
    console.error('[AUTH] activeOAuthProvider set apple at handler start (activeProvider=apple)')
    if (signInWatchdogRef.current) {
      clearTimeout(signInWatchdogRef.current)
      signInWatchdogRef.current = null
    }
    try {
      if (googleBrowserDismissListenerRef.current) {
        await googleBrowserDismissListenerRef.current.remove()
        googleBrowserDismissListenerRef.current = null
      }
    } catch { /* ignore */ }

    // Preserve existing consent flow behavior if user is already authenticated
    if (user) {
      try {
        if (!hasAcceptedAll && activeDocs.length > 0) {
          await acceptAll()
        }
        navigate('/profile')
        activeOAuthProviderRef.current = null
        __activeOAuthProvider.current = null
        return
      } catch (error) {
        console.error('❌ V180.5: Failed to save consent:', error)
        toast.error('Failed to save consent. Please try again.')
        setIsLoading(false)
        setActiveAuthProvider(null)
        activeOAuthProviderRef.current = null
        __activeOAuthProvider.current = null
        return
      }
    }

    try {
      callbackReceivedRef.current = false
      // V402: Wrap appleSignIn() in 20s backstop timeout. The existing 45s watchdog is only
      // armed AFTER appleSignIn() resolves; if the hook itself hangs (Supabase signInWithOAuth
      // network stall, Browser.open never resolves, native plugin freeze), nothing else can
      // recover the spinner. Throw → fall through to outer catch which resets all UI state.
      console.log('[V402.AUTH-APPLE] APPLE_HOOK_CALL t=', Date.now())
      const authResult = await withTimeout(appleSignIn(), 20_000)
      console.log('[V402.AUTH-APPLE] APPLE_HOOK_RESOLVED hasError=', !!authResult?.error, 't=', Date.now())
      const authError = authResult?.error
      if (authError) {
        console.warn('[V402.AUTH-APPLE] APPLE_HOOK_ERROR code=', (authError as any)?.code, 'msg=', (authError as any)?.message)
        throw authError
      }
    } catch (error: unknown) {
      const isTimeout = error instanceof Error && error.message === 'timeout'
      const message = getAppleSignInErrorMessage(error)
      console.error('[V402.AUTH-APPLE] APPLE_HOOK_THROW isTimeout=', isTimeout, 'err=', error)
      if (import.meta.env.DEV) {
        console.log('[AppleReview] Apple auth handler error', {
          platform: Capacitor.getPlatform(),
          provider: 'apple',
          isTimeout,
        })
      }
      // V402: clear watchdog on any error path so we never leave a dangling timer
      if (signInWatchdogRef.current) {
        clearTimeout(signInWatchdogRef.current)
        signInWatchdogRef.current = null
      }
      console.log('[V402.AUTH-APPLE] APPLE_RESET reason=', isTimeout ? 'hook_timeout_20s' : 'hook_throw')
      activeOAuthProviderRef.current = null
      __activeOAuthProvider.current = null
      setError(message)
      setIsLoading(false)
      setActiveAuthProvider(null)
      resetAuthInProgress(isTimeout ? 'apple_hook_timeout_20s' : 'apple_hook_throw') // V404: clear hook lock
      toast.error(message, { duration: 6000 })
    }
  }

  // V209.7: Defer expensive paint styles until after first paint
  const [paintReady, setPaintReady] = React.useState(false);

  React.useEffect(() => {
    // Defer backdrop-blur and animations until after first paint
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setPaintReady(true);
      });
    });
  }, []);

  const shouldShowAppleButton = Capacitor.getPlatform() !== 'android'

  return (
    <div
      className="bn-auth-shell min-h-screen flex items-center justify-center p-4 relative"
      style={{
        // Explicit dark backdrop for iOS/Web/Android regardless of theme or prefers-color-scheme
        backgroundColor: '#020617',
        color: '#e5e7eb',
      }}
    >
      {/* Back + language selector header (Welcome/Auth page) */}
      <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between gap-2 px-3 pt-3">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleBack}
          className="min-w-0 shrink gap-2 text-white hover:bg-white/10 hover:text-white"
          aria-label={t('common.back')}
        >
          <ArrowLeft className="h-4 w-4 shrink-0 rtl:rotate-180" />
          <span className="truncate">{t('common.back')}</span>
        </Button>
        <LanguageSelector variant="compact" />
      </div>
      {/* V209.7: Static card first, add blur after paint */}
      {/* V209.9: Critical CSS shell class for instant paint */}
      <div
        className={`bn-auth-card ${paintReady ? 'backdrop-blur-sm' : ''} rounded-3xl p-8 w-full max-w-md text-center transition-opacity duration-200`}
        style={{
          backgroundColor: 'rgba(15,23,42,0.9)',
          border: '1px solid rgba(148,163,184,0.6)',
        }}
      >
        {/* Master logo - directly placed without circular wrapper */}
        <img
          src={AUTH_BRAND_ICON_SRC}
          alt="BlissNet"
          className="mx-auto mb-10 block h-40 w-40 object-contain object-center"
          aria-hidden="true"
        />

        {/* V209.9: Critical CSS shell classes for instant paint */}
        <h1 className="bn-auth-title text-3xl font-bold text-white mb-2">
          {t('welcome.title', { brand: 'BlissNet' })}
        </h1>
        <p className="bn-auth-subtitle text-slate-300 mb-5">
          {t('welcome.subtitle')}
        </p>
        <div className="mb-5 mt-0 px-1 text-center text-sm leading-relaxed text-slate-400">
          <p className="mb-2">
            <span className="font-semibold text-slate-200">Bliss</span>{' '}
            {t('welcome.brandBlissMeaning')}
          </p>
          <p>
            <span className="font-semibold text-slate-200">Net</span>{' '}
            {t('welcome.brandNetMeaning')}
          </p>
        </div>

        {/* 🎯 V184.1: TASK 1 - 18+ only messaging for Google Play compliance */}
        <div className="mb-6 rounded-xl p-3 flex items-start gap-2 bg-amber-500/15 border border-amber-400/60">
          <ShieldAlert className="h-5 w-5 text-amber-300 mt-0.5 flex-shrink-0" aria-hidden="true" />
          <p className="text-amber-100 text-sm font-semibold text-start">
            {t('safetyPage.warningTitle')}
          </p>
        </div>

        {/* V314.7: pi_debug-only banner – verdict, reason, stage, tokenLen, keys, checkId prefix, err (trunc 40) */}
        {piDebug && debugAudit && (
          <div className="mb-4 px-3 py-2 rounded-lg text-xs font-mono bg-slate-800/80 text-slate-300 border border-slate-600">
            <div>
              <span className="font-semibold">PI:</span> {debugAudit.verdict} · {debugAudit.reason}
              {debugAudit.stage && <span className="text-slate-400"> · stage:{debugAudit.stage}</span>}
            </div>
            <div className="text-slate-400">
              tokenLen:{debugAudit.tokenLen} · keys:{debugAudit.serviceResultKeys?.join(',') || 'none'}
              {debugAudit.checkId != null && debugAudit.checkId !== '' && (
                <> · id:{debugAudit.checkId.slice(0, 8)}</>
              )}
              {debugAudit.err && (
                <> · err:{debugAudit.err.slice(0, 40)}</>
              )}
            </div>
          </div>
        )}

        {/* V314.5 FINAL: pi_debug-only buttons — Test Play Integrity, Show last audit */}
        {piDebug && (
          <div className="mb-4 flex gap-2 justify-center flex-wrap">
            <button
              type="button"
              onClick={runIntegritySelfTest}
              disabled={piTestRunning}
              className="px-3 py-1.5 rounded-lg text-xs font-mono bg-slate-700 text-slate-200 border border-slate-500 hover:bg-slate-600 disabled:opacity-50"
            >
              {piTestRunning ? 'Running...' : 'Test Play Integrity'}
            </button>
            <button
              type="button"
              onClick={showLastAudit}
              className="px-3 py-1.5 rounded-lg text-xs font-mono bg-slate-700 text-slate-200 border border-slate-500 hover:bg-slate-600"
            >
              Show last audit
            </button>
          </div>
        )}

        {error && (
          <div className="bg-red-500/20 border border-red-400 text-red-100 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* V179.11: Explicit consent checkbox - includes all 4 legal docs - V206.5: Added Safety & Moderation Policy */}
        <div className="mb-6">
          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              className="mt-0.5 w-5 h-5 shrink-0 rounded border-gray-400 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 cursor-pointer"
            />
            <span className="text-xs leading-relaxed text-gray-300 group-hover:text-white transition-colors text-start">
              {t('welcome.agreementLead')}{' '}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/legal/terms');
                }}
                className="text-blue-400 hover:text-blue-300 underline mx-0.5"
              >
                {t('legalTitles.terms')}
              </button>
              ,{' '}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/legal/privacy');
                }}
                className="text-blue-400 hover:text-blue-300 underline mx-0.5"
              >
                {t('legalTitles.privacy')}
              </button>
              ,{' '}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/legal/guidelines');
                }}
                className="text-blue-400 hover:text-blue-300 underline mx-0.5"
              >
                {t('legalTitles.communityGuidelines')}
              </button>
              , {t('welcome.agreementAnd')}{' '}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/legal/safety');
                }}
                className="text-blue-400 hover:text-blue-300 underline mx-0.5"
              >
                {t('legalTitles.safety')}
              </button>
            </span>
          </label>
        </div>

        {/* OAuth CTAs: Apple is the first visible provider on iOS; Android remains Google-only. */}
        <div
          role="group"
          aria-label={t('welcome.oauthGroupLabel')}
          className="flex flex-col gap-3 isolate [contain:layout]"
        >
          {shouldShowAppleButton && (
            <button
              type="button"
              onClick={() => {
                console.error('[AUTH_BUTTON] APPLE button clicked')
                void handleAppleAuth()
              }}
              disabled={isLoading || consentLoading || consentSaving || emailSignInLoading || !agreedToTerms}
              aria-label={t('welcome.continueApple')}
              className="bn-auth-btn relative z-[1] mt-0 flex min-h-12 w-full items-center justify-center gap-3 bg-[#000000] ps-6 pe-6 font-semibold text-white rounded-xl transition-colors hover:bg-[#000000] disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
            >
              {activeAuthProvider === 'apple' && isLoading ? (
                <>
                  <Loader2 className="h-5 w-5 shrink-0 animate-spin" />
                  {t('welcome.connecting')}
                </>
              ) : (
                <>
                  <span
                    className="inline-flex h-12 w-12 shrink-0 [&_svg]:h-full [&_svg]:w-full"
                    aria-hidden="true"
                    dangerouslySetInnerHTML={{ __html: appleLogoSvg }}
                  />
                  {t('welcome.continueApple')}
                </>
              )}
            </button>
          )}

          <button
            type="button"
            onClick={() => {
              console.error('[AUTH_BUTTON] GOOGLE button clicked')
              void handleGoogleAuth()
            }}
            disabled={isLoading || consentLoading || consentSaving || emailSignInLoading || !agreedToTerms}
            aria-label={t('welcome.continueGoogle')}
            className="bn-auth-btn relative z-[2] mt-0 w-full bg-white text-gray-800 font-semibold py-4 px-6 rounded-xl hover:bg-gray-100 transition-colors flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
          >
            {activeAuthProvider === 'google' && isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {t('welcome.connecting')}
              </>
            ) : user ? (
              t('welcome.continueToApp')
            ) : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                {t('welcome.continueGoogle')}
              </>
            )}
          </button>
        </div>

        {/* Reviewer fallback: explicit /auth?review route only. */}
        {showEmailSignInSection && (
          <div className="mt-6 border-t border-slate-600/80 pt-6 text-left">
            <button
              type="button"
              onClick={() => setShowEmailSignIn((v) => !v)}
              disabled={isLoading || consentSaving || emailSignInLoading}
              className="flex w-full items-center justify-between gap-2 rounded-lg px-1 py-2 text-sm font-medium text-slate-300 transition-colors hover:text-white disabled:opacity-50"
              aria-expanded={showEmailSignIn}
            >
              <span>Sign in with email</span>
              {showEmailSignIn ? (
                <ChevronUp className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
              ) : (
                <ChevronDown className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
              )}
            </button>
            {showEmailSignIn && (
              <div className="mt-3">
                <EmailSignInForm
                  email={emailSignInEmail}
                  setEmail={setEmailSignInEmail}
                  password={emailSignInPassword}
                  setPassword={setEmailSignInPassword}
                  onSubmit={handleEmailSignIn}
                  loading={emailSignInLoading || isLoading}
                  variant="authDark"
                />
              </div>
            )}
          </div>
        )}

      </div>

      {/* 🚀 V209.4: Lazy-loaded modals with Suspense - only load when user clicks links */}
      {/* Modals - V179.11: Added GuidelinesModal - V206.5: Added SafetyModal */}
      {showTerms && (
        <Suspense fallback={null}>
          <TermsModal isOpen={showTerms} onClose={handleCloseTerms} />
        </Suspense>
      )}
      {showPrivacy && (
        <Suspense fallback={null}>
          <PrivacyModal isOpen={showPrivacy} onClose={handleClosePrivacy} />
        </Suspense>
      )}
      {showGuidelines && (
        <Suspense fallback={null}>
          <GuidelinesModal isOpen={showGuidelines} onClose={handleCloseGuidelines} />
        </Suspense>
      )}
      {showSafety && (
        <Suspense fallback={null}>
          <SafetyModal isOpen={showSafety} onClose={handleCloseSafety} />
        </Suspense>
      )}
    </div>
  );
};

export default AuthenticationStep;
